import React, { useMemo, useState, useEffect, useContext } from 'react';
import { BrowserRouter as Router, Route } from 'react-router-dom';
import { IconSprite } from '@ellucian/ds-icons/lib';
import NewApplication from './components/ApplicantHome/NewApplication';
import ApplicantHome from './components/ApplicantHome/ApplicantHome';
import RetrievalForm from './components/RetrievalForm/RetrievalForm';
import RetrievalSubmitted from './components/RetrievalForm/RetrievalSubmitted';
import { IntlProvider, useIntl } from 'react-intl';
import messages from './resources';
import UserContext from './components/UserContext';
import Header from './components/Header/Header';
import { spacing80, spacing40, spacing90, fountain100, colorBackgroundDefault } from '@ellucian/react-design-system/core/styles/tokens';
import {makeStyles, useMediaQuery } from '@ellucian/react-design-system/core';
import { useTheme } from '@mui/material';
import { ApiProvider, useApi } from './components/ApiContext';
import Timeout from './components/Timeout/Timeout';
import { routes, CONSTANTS } from './components/Helpers/Configuration';
import { SnackbarProvider } from './components/SnackbarContext/SnackbarContext';
import Application from './components/ApplicationFormLayout/Application';
import RootContext from './components/RootContext/RootContext';
import { createEDSTheme } from '@ellucian/react-design-system/core/themes';
import InvalidApplicantLink from './components/InvalidApplicantLink/invalidApplicantLink';
import ExternalApplication from './components/ApplicationFormLayout/ExternalApplication/ExternalApplication';
import parseTheme from './utilities/parseTheme';
import GlobalLocaleContext from './components/GlobalLocale/GlobalLocaleContext';
import InvalidShareUrl from './components/InvalidShareUrl/InvalidShareUrl';
import classnames from 'classnames';
import isInIframe from './utilities/isInIframe';
import LanguageDropdown from './components/LanguageDropdown/LanguageDropdown';
import getQueryParam from './utilities/getQueryParam';
import Footer from './components/Footer';
import EDSContent from './EDSContent';
import rtlDetect from 'rtl-detect';
import { getDisplayForLocale } from './components/Helpers/messagesHelpers';
import MFAWrapper from './components/MFAAuth/MFAWrapper';

const useStyles = makeStyles(theme => ({
    content: {
        margin: `${spacing80} auto 0 auto`,
        maxWidth: '1280px',
        '&.embedded': {
            marginTop: 0,
        }
    },
    inquiryContent: {
        margin: `${spacing80} auto 0 auto`,
        maxWidth: '800px',
    },
    copyright: {
        textAlign: 'center',
        marginBottom: spacing40,
    },
    root: {},
    [theme.breakpoints.down('sm')]: {
        root: {
            marginBottom: spacing90,
        },
    },
    languageDropdownContainer: {
        display: 'flex',
    },
    languageDropdownRoot: {
        marginLeft: 'auto',
    },
    overlayDisable: {
        backgroundColor: fountain100,
        position: 'fixed',
        width: '100%',
        height: '100%',
        zIndex: '1000',
        top: '0px',
        left: '0px',
        opacity: '0.5',
    },
    overlay: {
        backgroundColor: colorBackgroundDefault,
        position: 'fixed',
        width: '100%',
        height: '100%',
        zIndex: '1000',
        top: '0px',
        left: '0px',
    },
    loadingIndicator: {
        position: 'fixed',
        left: '50%',
        top: '50%',
    },
}));
const userCtxValue = {
    principle: window.PRELOADED_STATE?.user?.name,
    signOut: () => {
        window.location = '/logout';
    },
};
const baseTheme = createEDSTheme('light');

const AppContent = () => {
    const classes = useStyles();
    const api = useApi();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    //timeout handlers, force a timeout when the user had idle timed out and reset the timeout afterwards.
    const [isTimedout, setTimeout] = useState(false);
    const forceTimeout = async () => {
        await api.applicantLogout();
        if (isTimedout != true) {
            setTimeout(true);
        }
    };
    const handleReset = () => {
        if (isTimedout) {
            setTimeout(false);
        }
    };
    const isEmbedded = useMemo(() => isInIframe(), []);
    const { globalLocale, updateDisplayLocale } = useContext(GlobalLocaleContext);
    const { formatMessage } = useIntl();

    useEffect(() => {
        updateDisplayLocale(getDisplayForLocale(globalLocale, formatMessage));
    }, [globalLocale, formatMessage]);

    const contentClasses = useMemo(() => {
        return classnames(classes.content, {
            embedded: isEmbedded || isMobile,
        });
    }, [classes, isEmbedded, isMobile]);

    const showLanguageDropDown = useMemo(() => isEmbedded || isMobile, [isEmbedded, isMobile]);
    return (
        <ApiProvider>
            <UserContext.Provider value={userCtxValue}>
                <IconSprite />
                <Router>
                    <Timeout forceTimeout={forceTimeout} isTimedout={isTimedout} handleReset={handleReset}/>
                    <div className={classes.root}>
                        {!isEmbedded &&
                            <Header showUtilityBar={false} />
                        }
                        {showLanguageDropDown &&
                            <div className={classes.languageDropdownContainer}>
                                <LanguageDropdown classes={{ root: classes.languageDropdownRoot }} />
                            </div>
                        }
                        <div className={contentClasses}>
                            <Route exact path={routes.home} component={ApplicantHome}/>
                            <Route exact path={routes.newApplication} component={NewApplication}/>
                        </div>
                        <Route
                            exact
                            path={routes.invalidShareUrl}
                            render={(props) => <InvalidShareUrl {...props} />}
                        />
                        <Route path={routes.externalApplication} render={ (props) => <ExternalApplication {...props} isTimedout={isTimedout} /> } />
                        <Route
                            exact
                            path={routes.retrieval}
                        >
                            <RetrievalForm isTimedout={isTimedout}/>
                        </Route>
                        <Route exact path={routes.retrievalSubmitted}>
                            <RetrievalSubmitted commType={CONSTANTS.communicationTypes.email} />
                        </Route>
                        <Route path={routes.applicationFormSupplementalForm}>
                            <Application />
                        </Route>
                        <Route
                            exact
                            path={routes.invalidApplicantLink}
                            render={(props) => <InvalidApplicantLink {...props} />}
                        />
                        <Route
                            path={routes.applicationForm}
                            component={Application}
                        />
                        <Route
                            path={routes.totp}
                            component={MFAWrapper}
                        />
                        {!isEmbedded && <Footer />}
                    </div>
                </Router>

            </UserContext.Provider>
        </ApiProvider>
    );
};

// Swap message keys
(() => {
    const settingMessages = window.PRELOADED_STATE?.globalSettings?.settingMessages;
    let settings = window.PRELOADED_STATE?.globalSettings?.generalSettings;

    if (!settingMessages || !settingMessages.languages) {
        throw new Error('Unable to read setting messages from preloaded state');
    }
    else if (!settings) {
        throw new Error('Unable to read general settings from preloaded state');
    }

    if (window.PRELOADED_STATE?.globalSettings?.applicationRetrievalSettings) {
        settings = settings.concat(
            Object.values(window.PRELOADED_STATE.globalSettings.applicationRetrievalSettings)
        );
    }

    const locales = Object.keys(settingMessages.languages);
    let newMessages;
    locales.forEach((locale) => {
        newMessages = window.PRELOADED_STATE.globalSettings.settingMessages.languages[locale]?.messages;
        settings.map((setting) => {
            if (setting.localized) {
                newMessages[setting.settingName] = newMessages[setting.settingValue];
                delete newMessages[setting.settingValue];
            }
            window.PRELOADED_STATE.globalSettings.settingMessages.languages[locale].messages = newMessages;
        });
    });
})();

const initialLocale = (() => {
    const settingMessages = window.PRELOADED_STATE.globalSettings.settingMessages;
    const locales = Object.keys(settingMessages.languages);
    let selectedLocale;
    const queryParamLocale = getQueryParam('localeCode');
    if (queryParamLocale && locales.includes(queryParamLocale)) {
        selectedLocale = queryParamLocale;
    } else {
        const { languages } = navigator; // Gets browser languages
        for (const browserLocale of languages) {
            const browserLang = browserLocale.split('-')[0];
            selectedLocale = locales.includes(browserLocale) && browserLocale;
            selectedLocale = selectedLocale || (locales.includes(browserLang) && browserLang);
            selectedLocale = selectedLocale || locales.find(x => x.split('-')[0] === browserLocale);
            if (selectedLocale) { break; }
        }
    }
    selectedLocale = selectedLocale || window.PRELOADED_STATE.globalSettings.defaultLanguage;
    let dir = 'ltr';
    if (rtlDetect.isRtlLang(selectedLocale)) {
        dir = 'rtl';
    }
    
    return {
        locale: selectedLocale,
        dir
    };
})();

const getMessagesForLocale = (locale) => {
    const initial = initialLocale.locale;

    return {
        // Default any messages from the initial locale.
        ...window.PRELOADED_STATE.globalSettings.settingMessages.languages[initial].messages,
        ...(messages[initial] || messages[initial.split('-')?.[0]]),
        ...window.PRELOADED_STATE.globalSettings.settingMessages.languages[locale].messages,
        ...(messages[locale] || messages[locale.split('-')?.[0]])
    };
};

initialLocale.messages = getMessagesForLocale(initialLocale.locale);

const App = () => {
    const [eteTheme, setEteTheme] = useState(window.PRELOADED_STATE?.templateTheme || window.PRELOADED_STATE?.globalTheme);
    const [theme, setTheme] = useState(parseTheme(eteTheme));
    const [globalTheme,] = useState(window.PRELOADED_STATE?.globalTheme);
    const [isAutosaving, setIsAutosaving] = useState(false);
    const rootCtx = {
        eteTheme,
        globalTheme,
        theme,
        isAutosaving,
        setTheme: (theme, eteTheme = {}) => {
            setTheme(theme);
            setEteTheme(eteTheme);
        },
        clearTheme: () => {
            if (globalTheme) {
                setTheme(parseTheme(globalTheme));
                setEteTheme(globalTheme);
            }
            else {
                setTheme(baseTheme);
                setEteTheme(null);
            }
        },
        setIsAutosaving
    };
    const [globalLocale, setGlobalLocale] = useState(initialLocale.locale);
    const [displayLocale, setDisplayLocale] = useState(''); // App content sets this.
    const [localeDirection, setLocaleDirection] = useState(initialLocale.dir);
    const [localizedMessages, setLocalizedMessages] = useState(initialLocale.messages);
    const globalLocaleCtx = {
        globalLocale,
        displayLocale,
        localeDirection,
        updateGlobalLocale: (locale) => {
            setGlobalLocale(locale);
            const newLocalizedMessages = getMessagesForLocale(locale);
            setLocalizedMessages(newLocalizedMessages);
            let dir = 'ltr';
            if (rtlDetect.isRtlLang(locale)) {
                dir = 'rtl';
            }
            if (localeDirection !== dir) {
                setLocaleDirection(dir);
            }
        },
        updateDisplayLocale: (display) => setDisplayLocale(display),
    };
    return (
        <RootContext.Provider value={rootCtx}>
            <div dir={localeDirection}>
                <GlobalLocaleContext.Provider value={globalLocaleCtx}>
                    <IntlProvider locale={globalLocale || navigator.language} messages={localizedMessages}>
                        <EDSContent
                            theme={theme}
                            globalLocale={globalLocale}
                            localeDirection={localeDirection}
                        >
                            <SnackbarProvider>
                                <AppContent />
                            </SnackbarProvider>
                        </EDSContent>
                    </IntlProvider>
                </GlobalLocaleContext.Provider>
            </div>
        </RootContext.Provider>
    );
};

export default App;